import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Building2, MapPin, Mail, Phone } from 'lucide-react';
import type { HoldedContactMatch } from '@/hooks/useHoldedContactLookup';

interface Props {
  contact: HoldedContactMatch;
  onUseData: () => void;
  onIgnore: () => void;
}

export const HoldedContactMatchCard: React.FC<Props> = ({ contact, onUseData, onIgnore }) => {
  return (
    <Card className="border-amber-400/60 bg-amber-50/60 dark:bg-amber-500/5">
      <CardContent className="pt-4 pb-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-amber-500/15 p-2">
            <CheckCircle2 className="h-5 w-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-sm">Contacto encontrado en Holded</h3>
              <Badge variant="outline" className="text-[10px]">CIF {contact.code}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Ya existe en tu cuenta de Holded. Puedes usar sus datos para autocompletar el formulario y enlazarlo, evitando duplicados.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs pl-12">
          <div className="flex items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-medium truncate">{contact.name || '—'}</span>
          </div>
          {(contact.address || contact.city) && (
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="truncate">
                {[contact.address, contact.postalCode, contact.city].filter(Boolean).join(', ')}
              </span>
            </div>
          )}
          {contact.email && (
            <div className="flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="truncate">{contact.email}</span>
            </div>
          )}
          {contact.phone && (
            <div className="flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="truncate">{contact.phone}</span>
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-end pt-1">
          <Button size="sm" variant="ghost" onClick={onIgnore}>
            Crear nuevo igualmente
          </Button>
          <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={onUseData}>
            Usar estos datos y enlazar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
